//////////////////////////////////////////
// src/assets/scripts/top/module/sphere.js
//////////////////////////////////////////
import {
  PlaneGeometry,
  TextureLoader,
  Mesh,
  ShaderMaterial,
  MeshBasicMaterial,
  SRGBColorSpace,
  ClampToEdgeWrapping,
  Vector2,
  VideoTexture,
  LinearFilter,
  DataTexture,
  Color,
  Group,
  RepeatWrapping
} from "three";

import { gsap } from "gsap";
import * as dat from "dat.gui";
import vertexShader from "../shaders/vertexshader.vert";
import fragmentShader from "../shaders/fragmentshader.frag";

function getAssetPath(relative) {
  const hostname = window.location.hostname;
  const isLocal =
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.endsWith(".local");

  if (isLocal) return `/dist/assets/images/${relative}`;
  return `/wp/wp-content/themes/my-theme-threeJS2/dist/assets/images/${relative}`;
}

export default class Sphere {
  constructor(stage) {
    this.stage = stage;

    this.group = new Group();

    // overlay (scene1)
    this.bgMesh = null;
    this.logoMesh = null;

    // main (A/B separate)
    this.meshA = null;
    this.meshB = null;
    this.uniformsA = null;
    this.uniformsB = null;

    this._onResize = null;
    this.tl = null;

    this._targetScale = new Vector2(1, 1);

    // textures
    this._texA = null;
    this._texB = null; // Bがimageモードのとき保持
    this._texLogo = null;

    // video
    this._videoEl = null;
    this._videoTex = null;
    this._mediaBReady = false;
    //this._bMode = "video"; // "video" | "image"（必要なら外から上書き）

    // start gating
    this._ready = { logo: false, image: false };
    this._sequenceStarted = false;

    this.loader = new TextureLoader();

    // ---- scene1 colors ----
    this._sceneBgColor = new Color("#FAECDF"); // ベージュ背景
    this._logoBlack = new Color("#000000");    // 初期：黒ロゴ
    this._logoWhite = new Color("#FFFFFF");    // 変化後：白ロゴ

    this._uTimeShared = { value: 0.0 };
    this._uWaveShared = { value: 1.0 };
    this._uTransShared = { value: 0.0 };
    this.progressMesh = null;
    
  }

  init() {
    this._build();
    //this._setDev();
  }

  // -----------------------------
  // build meshes/materials
  // -----------------------------
  _build() {
    // ---- 0) group ----
    this.stage.scene.add(this.group);

    // ---- 1) scene1 bg (beige) ----
    {
      const geo = new PlaneGeometry(1, 1, 1, 1);
      const mat = new MeshBasicMaterial({
        color: this._sceneBgColor.clone(), // ★ベージュ
        transparent: true,
        opacity: 1,
        depthWrite: false,
      });
      this.bgMesh = new Mesh(geo, mat);
      this.bgMesh.position.set(0, 0, 0.004);
      this.group.add(this.bgMesh);
    }

    // ---- 2) logo (logo.svg) ----
    {
      const geo = new PlaneGeometry(1, 1, 1, 1);
      const mat = new MeshBasicMaterial({
        transparent: true,
        opacity: 1, // ★シーン1から表示する
        depthWrite: false,
        //color: this._logoBlack.clone(), // ★最初は黒（mapに乗算）
      });
      this.logoMesh = new Mesh(geo, mat);
      this.logoMesh.position.set(0, 0, 0.005);
      this.group.add(this.logoMesh);
    }

    // ---- 2.5) progress line (scene1) ----
    {
      const geo = new PlaneGeometry(1, 1, 1, 1);
      const mat = new MeshBasicMaterial({
        color: new Color("#393537"),
        transparent: true,
        opacity: 1,
        depthWrite: false,
      });

      this.progressMesh = new Mesh(geo, mat);
      this.progressMesh.position.set(0, 0, 0.006); // 一番手前に
      this.progressMesh.visible = false;
      this.group.add(this.progressMesh);

      // 内部キャッシュ（resizeで更新）
      this._progress = { fullW: 1, h: 1 };
    }

    // ---- 3) shared geo for A/B ----
    const SEG = 64;
    const sharedGeo = new PlaneGeometry(1, 1, SEG, SEG);

    // uTexB null事故防止（透明）
    const dummyB = new DataTexture(new Uint8Array([0, 0, 0, 0]), 1, 1);
    dummyB.needsUpdate = true;

    this._uWaveShared = { value: 0.0 };

    // ---- 4) uniforms factory ----
    const makeUniforms = () => ({
      uTexA: { value: null },
      uTexB: { value: dummyB },
      uRes: { value: new Vector2(1, 1) },

      // ★ここを共有
      uTime: this._uTimeShared,
      uWave: this._uWaveShared,
      uTrans: this._uTransShared,

      uOpacity: { value: 1.0 },

      uDispTex: { value: null },
      uDispStrength: { value: 0.085 },
      uDispScale: { value: 0.3 },
      uDispSpeed: { value: 0.03 },

      uMixT: { value: 0.0 }
    });
  

    // ---- 5) material A ----
    {
      this.uniformsA = makeUniforms();
      //this.uniformsA.uMix.value = 0.0;
      //this.uniformsA.uOverlay.value = 0.0;
      this.uniformsA.uOpacity.value = 1.0;

      const matA = new ShaderMaterial({
        vertexShader,
        fragmentShader,
        uniforms: this.uniformsA,
        transparent: true,
        depthWrite: false,
      });

      this.meshA = new Mesh(sharedGeo, matA);
      this.meshA.position.set(0, 0, 0.002);//2
      this.meshA.visible = false;
      this.group.add(this.meshA);
    }

    // ---- 6) material B ----
    {
      this.uniformsB = makeUniforms();
      //this.uniformsB.uMix.value = 1.0;
      //this.uniformsB.uOverlay.value = 1.0; // 円外透明
      this.uniformsB.uOpacity.value = 0.0; // フェードで出す

      const matB = new ShaderMaterial({
        vertexShader,
        fragmentShader,
        uniforms: this.uniformsB,
        transparent: true,
        depthWrite: false,
      });

      this.meshB = new Mesh(sharedGeo, matB);
      this.meshB.position.set(0, 0, 0.003);//3
      this.meshB.visible = false;
      this.group.add(this.meshB);
    }

    // ---- load assets ----
    this._loadLogo();
    this._loadImageA();
    this._loadDisp();

    this._onResize = () => this.onResize();
    window.addEventListener("resize", this._onResize);
  }

  _tryStartSequence() {
    if (this._sequenceStarted) return;
    if (!this._ready.logo || !this._ready.image) return;

    this._sequenceStarted = true;
    this._playSequence();
  }

  _loadLogo() {
    const logoPath = getAssetPath("top/logo.png");

    this.loader.load(
      logoPath,
      (tex) => {
        tex.colorSpace = SRGBColorSpace;
        tex.wrapS = tex.wrapT = ClampToEdgeWrapping;

        this._texLogo = tex;
        this.logoMesh.material.map = tex;
        this.logoMesh.material.needsUpdate = true;

        // 初期：黒ロゴ（念押し）
        this.logoMesh.material.opacity = 1.0;
        this.logoMesh.material.color.copy(this._logoBlack);

        this.onResize();

        this._ready.logo = true;
        this._tryStartSequence();
      },
      undefined,
      (err) => console.warn("Logo load error:", logoPath, err)
    );
  }

  _loadImageA() {
    const imgPath = getAssetPath("top/mv.webp");

    this.loader.load(
      imgPath,
      (tex) => {
        tex.colorSpace = SRGBColorSpace;
        tex.wrapS = tex.wrapT = ClampToEdgeWrapping;

        this._texA = tex;

        // AにもBにもAを渡しておく（shader内参照があっても破綻しない）
        this.uniformsA.uTexA.value = tex;
        this.uniformsB.uTexA.value = tex;

        this.onResize();

        this._ready.image = true;
        this._tryStartSequence();
      },
      undefined,
      (err) => console.warn("Image load error:", imgPath, err)
    );
  }

  _loadDisp() {
    const dispPath = getAssetPath("top/clouds.jpg");

    this.loader.load(
      dispPath,
      (tex) => {
        tex.wrapS = tex.wrapT = RepeatWrapping; // ★重要（import追加してもOK）
        // もし THREE がないなら import { RepeatWrapping } from "three"; して tex.wrapS = tex.wrapT = RepeatWrapping;

        // B側に必須（A側にも入れといてOK）
        this.uniformsA.uDispTex.value = tex;
        this.uniformsB.uDispTex.value = tex;
      },
      undefined,
      (err) => console.warn("Disp load error:", dispPath, err)
    );
  }

  // -----------------------------
  // timeline sequence
  // -----------------------------

  _playSequence() {
    if (!this.uniformsA || !this.uniformsB || !this._texA) return;
    if (this.tl) this.tl.kill();

    // timings
    const LOGO_HOLD = 1.0;

    const LINE_DUR = 1.8;

    // A
    const A_ZOOM_DUR = 1.2;
    const A_WAVE_DUR = 1.2;
    const A_HOLD = 0.5;

    // B
    const B_WAIT = 0.75;     // 1.8
    const B_FADE = 0.95;    // .35
    const B_WAVE_DUR = 1.2;

    const WAVE_END = 0.05;  // 最終残す波（0なら停止）
    const zoomStart = 1.15;

    const baseX = this._targetScale.x;
    const baseY = this._targetScale.y;

    // ---- 初期化（replayで前状態が残らないように）----
    // 表示フェード
    this.uniformsA.uOpacity.value = 0.0;
    //this.uniformsB.uOpacity.value = 0.0;

    // ★Wave/Trans は shared を使う前提なので「sharedだけ」初期化
    if (this._uWaveShared) this._uWaveShared.value = 1.0;
    if (this._uTransShared) this._uTransShared.value = 0.0;

    // 念のため（uniform個別に残ってても暴れないように0へ）
    if (this.uniformsA.uTrans) this.uniformsA.uTrans.value = 0.0;
    if (this.uniformsB.uTrans) this.uniformsB.uTrans.value = 0.0;

    if (this.meshA) {
      this.meshA.visible = false;
      this.meshA.scale.set(baseX, baseY, 1);
    }
    if (this.meshB) {
      this.meshB.visible = false; // ★replay残り防止
      this.meshB.scale.set(baseX, baseY, 1);
    }

    this.tl = gsap.timeline({
      defaults: { overwrite: "auto" },
      onComplete: () => {
        document.body.classList.add("load_finish");
        //window.dispatchEvent(new Event("mv:ready"));
        setTimeout(() => window.dispatchEvent(new Event("mv:ready")), 650);
      },
    });

    this.tl
      // --- シーン1（今のまま） ---
      .to({}, { duration: LOGO_HOLD })

      // --- progress line ---
      .call(() => {
        if (!this.progressMesh) return;

        // 左端固定で伸ばすため：scaleXとpositionXをセットでやる
        const fullW = this._progress?.fullW ?? this.progressMesh.scale.x;

        // いったん0幅に
        this.progressMesh.visible = true;
        this.progressMesh.scale.x = 0.0001;

        // 左端位置（中心原点なので、左端 = -fullW/2）
        // 0幅のときは中心を左端に置く（= -fullW/2）
        this.progressMesh.position.x = -fullW * 0.5;
      }, null, ">")

      .to(this.progressMesh.scale, {
        x: () => this._progress.fullW,
        duration: LINE_DUR,
        //ease: "sine.inOut",
        ease: "power3.inOut",
        onUpdate: () => {
          // scaleが伸びると中心が真ん中に寄るので、左端固定に見えるようにxも動かす
          const fullW = this._progress.fullW;
          const curW = this.progressMesh.scale.x;
          this.progressMesh.position.x = -fullW * 0.5 + curW * 0.5;
        }
      }, ">")

      // === A START（ラベルで固定）===
      .addLabel("A_START", ">")

      // A準備（同フレームで確定）
      .set(this.meshA, { visible: true }, "A_START")
      .set(this.uniformsA.uOpacity, { value: 0.0 }, "A_START")
      .set(this.meshA.scale, { x: baseX * zoomStart, y: baseY * zoomStart }, "A_START")
      .set(this.progressMesh.material, { opacity: 0 }, "A_START")

      // ★A開始：shared wave を強に
      .set(this._uWaveShared, { value: 1.0 }, "A_START")
      // ★A開始：shared trans は常に0（遷移はB_STARTから）
      .set(this._uTransShared, { value: 0.0 }, "A_START")

      // 背景＋ロゴをフェードアウト
      .to(this.bgMesh.material, { opacity: 0, duration: 0.45, ease: "sine.inOut" }, "A_START")
      .to(this.logoMesh.material, { opacity: 0, duration: 0.45, ease: "sine.inOut" }, "A_START")

      // A表示（パッと）
      .set(this.uniformsA.uOpacity, { value: 1.0 }, "A_START+=0.01")

      // A：波 強→微小（★sharedのみ動かす）
      .to(
        this._uWaveShared,
        { value: WAVE_END, duration: A_WAVE_DUR, ease: "sine.out" },
        "A_START"
      )

      // A：拡大→等倍
      .fromTo(
        this.meshA.scale,
        { x: baseX * zoomStart, y: baseY * zoomStart },
        { x: baseX, y: baseY, duration: A_ZOOM_DUR, ease: "sine.out", immediateRender: false },
        "A_START"
      )

      // A静止
      .to({}, { duration: A_HOLD })

      // Bまで待つ
      .to({}, { duration: B_WAIT })

      // === B START ===
      .addLabel("B_START", ">")
      .call(() => this._prepareVideo(), null, "B_START")

      // Bを見える準備（ここで uWave を 1 に戻さない！）
      .set(this.meshB, { visible: true }, "B_START")
      .set(this.uniformsB.uOpacity, { value: 0.0 }, "B_START")
      // ★追加：BメッシュはBを出す
      .set(this.uniformsB.uMixT, { value: 1.0 }, "B_START")

      // Bフェード
      .to(this.uniformsB.uOpacity, { value: 1.0, duration: B_FADE, ease: "sine.out" }, "B_START")

      // ★A→B 遷移（ディストーション）：shared trans だけ動かす
      .set(this._uTransShared, { value: 0.0 }, "B_START")
      .to(this._uTransShared, { value: 1.0, duration: 1.35, ease: "sine.inOut" }, "B_START")

      // （任意）波キック：sharedにだけ当てる（ギュン感消し）
      // ※いらなければこの2行を丸ごと削除してOK
      .to(this._uWaveShared, { value: 1.0, duration: 0.12, ease: "sine.out" }, "B_START")
      .to(this._uWaveShared, { value: 0.012, duration: B_WAVE_DUR, ease: "power1.inOut" }, "B_START+=0.10");
  }



  _prepareVideo() {
      // すでに何か入ってたら二重生成しない
      if (this._mediaBReady) return;
  
      // ▼ ここだけ切り替え（あとで外から渡してもOK）
      const MODE = this._bMode || "image"; // "video" | "image"
  
      if (MODE === "image") {
        const imgPath = getAssetPath("top/mv_movie.jpg");
  
        this.loader.load(
          imgPath,
          (tex) => {
            console.log("[B image loaded]", imgPath, tex.image?.width, tex.image?.height);
  
            tex.colorSpace = SRGBColorSpace;
            tex.wrapS = tex.wrapT = ClampToEdgeWrapping;
            tex.needsUpdate = true; // ★念のため
  
            this._texB = tex;
            this.uniformsB.uTexB.value = tex;
  
            // ★これで「Bが見える状態」に強制する（切り分け用）
            //this.uniformsB.uOpacity.value = 1.0;
            //this.uniformsB.uMix.value = 1.0;      // B側を表示（あなたの構成次第だけど）
            //this.uniformsB.uCircle.value = 1.0;   // 円を最大
            //this.uniformsB.uWave.value = 0.0;     // 波は止めても良い
  
            this._mediaBReady = true;
          },
          undefined,
          (err) => console.warn("Image(B) load error:", imgPath, err)
        );
        return;
      }
  
  
      // ---------- video ----------
      if (this._videoTex) {
        // 既に作られてるなら差すだけ
        this.uniformsB.uTexB.value = this._videoTex;
        this._mediaBReady = true;
        return;
      }
  
      const videoPath = getAssetPath("top/sample.mp4");//sample.mp4
  
      const video = document.createElement("video");
      video.src = videoPath;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.autoplay = true;
      video.preload = "auto";
  
      // iOS/Chrome対策：playは失敗してもOK（後でユーザー操作で再開される）
      video.play().catch(() => { });
  
      const vtex = new VideoTexture(video);
      vtex.minFilter = LinearFilter;
      vtex.magFilter = LinearFilter;
      vtex.generateMipmaps = false;
      vtex.colorSpace = SRGBColorSpace;
  
      this._videoEl = video;
      this._videoTex = vtex;
  
      // ★B側に差す
      this.uniformsB.uTexB.value = vtex;
      this._mediaBReady = true;
    }

  // -----------------------------
  // cover scale
  // -----------------------------
  _applyCoverScale(mesh, texForAspect) {
    const cam = this.stage.camera;
    const renderer = this.stage.renderer;

    const w = renderer.domElement.clientWidth;
    const h = renderer.domElement.clientHeight;
    const viewAspect = w / h;

    const imgW = texForAspect?.image?.width || 1;
    const imgH = texForAspect?.image?.height || 1;
    const imgAspect = imgW / imgH;

    const planeZ = mesh.position.z;
    const dist = Math.abs(cam.position.z - planeZ);

    const vFov = (cam.fov * Math.PI) / 180;
    const viewH = 2 * Math.tan(vFov / 2) * dist;
    const viewW = viewH * viewAspect;

    let scaleX = viewW;
    let scaleY = viewH;

    // cover
    if (viewAspect > imgAspect) {
      scaleY = viewH;
      scaleX = viewH * imgAspect;
      const k = viewW / scaleX;
      scaleX *= k;
      scaleY *= k;
    } else {
      scaleX = viewW;
      scaleY = viewW / imgAspect;
      const k = viewH / scaleY;
      scaleX *= k;
      scaleY *= k;
    }

    const safe = 1.02;
    mesh.scale.set(scaleX * safe, scaleY * safe, 1);
    return { x: mesh.scale.x, y: mesh.scale.y };
  }

  _applyCoverPlane(mesh) {
    const cam = this.stage.camera;
    const r = this.stage.renderer.domElement;

    const w = r.clientWidth;
    const h = r.clientHeight;
    const viewAspect = w / h;

    const planeZ = mesh.position.z;
    const dist = Math.abs(cam.position.z - planeZ);
    const vFov = (cam.fov * Math.PI) / 180;

    const viewH = 2 * Math.tan(vFov / 2) * dist;
    const viewW = viewH * viewAspect;

    const safe = 1.02;
    mesh.scale.set(viewW * safe, viewH * safe, 1);
    mesh.position.set(0, 0, mesh.position.z);
  }

  onResize() {
    const r = this.stage.renderer.domElement;

    // uRes
    if (this.uniformsA) this.uniformsA.uRes.value.set(r.clientWidth, r.clientHeight);
    if (this.uniformsB) this.uniformsB.uRes.value.set(r.clientWidth, r.clientHeight);

    // bg cover
    if (this.bgMesh) this._applyCoverPlane(this.bgMesh);


    // logo size
    if (this.logoMesh) {
      const isSP = window.innerWidth <= 768;

      const s = Math.min(r.clientWidth, r.clientHeight);

      // ★ここでPC/SP分岐
      const ratio = isSP ? 0.26 : 0.16;//sp:pc

      const targetPx = s * ratio;
      const world = this._pxToWorld(targetPx);

      const iw = this._texLogo?.image?.width ?? 1;
      const ih = this._texLogo?.image?.height ?? 1;
      const aspect = iw / ih;

      this.logoMesh.scale.set(world * aspect, world, 1);
      this.logoMesh.position.set(0, 0, 0.04);
    }


    // main cover (A基準)
    if (this._texA && this.meshA && this.meshB) {
      const sA = this._applyCoverScale(this.meshA, this._texA);
      this._applyCoverScale(this.meshB, this._texA);
      this._targetScale.set(sA.x, sA.y);
    }

    // progress line
    if (this.progressMesh) {
      const cam = this.stage.camera;
      const r = this.stage.renderer.domElement;

      const w = r.clientWidth;
      const h = r.clientHeight;
      const viewAspect = w / h;

      const z = this.progressMesh.position.z;
      const dist = Math.abs(cam.position.z - z);
      const vFov = (cam.fov * Math.PI) / 180;
      const viewH = 2 * Math.tan(vFov / 2) * dist;
      const viewW = viewH * viewAspect;

      const lineH = this._pxToWorld(1); // 1pxをワールドへ

      // フル幅をキャッシュ
      this._progress.fullW = viewW * 1.02;
      this._progress.h = lineH;

      // 形（geometry 1x1 を scale で）
      // 初期は「0幅」でも位置が崩れないように、xはあとでTLで制御する前提
      this.progressMesh.scale.set(this._progress.fullW, lineH, 1);

      // 画面最上部（中心基準なので半分引く）
      this.progressMesh.position.y = (viewH * 0.5) - (lineH * 0.5);
    }
  }

  _pxToWorld(px) {
    const cam = this.stage.camera;
    const r = this.stage.renderer.domElement;
    const planeZ = 0;
    const dist = Math.abs(cam.position.z - planeZ);
    const vFov = (cam.fov * Math.PI) / 180;
    const viewH = 2 * Math.tan(vFov / 2) * dist;
    return px * (viewH / r.clientHeight);
  }

  _setDev() {
    const gui = new dat.GUI();

    if (this.uniformsA) {
      gui.add(this.uniformsA.uCircle, "value", 0, 1, 0.001).name("A uCircle");
      gui.add(this.uniformsA.uWave, "value", 0, 1, 0.001).name("A uWave");
      gui.add(this.uniformsA.uOpacity, "value", 0, 1, 0.001).name("A uOpacity");
    }
    if (this.uniformsB) {
      gui.add(this.uniformsB.uCircle, "value", 0, 1, 0.001).name("B uCircle");
      gui.add(this.uniformsB.uWave, "value", 0, 1, 0.001).name("B uWave");
      gui.add(this.uniformsB.uOpacity, "value", 0, 1, 0.001).name("B uOpacity");
    }

    gui.add(this.uniformsA.uCircleStartPx, "value", 50, 900, 1).name("uCircleStartPx");
    gui.add(this.uniformsA.uCircleSoftPx, "value", 0, 400, 1).name("uCircleSoftPx");
    gui.add(this.uniformsA.uRingPx, "value", 0, 160, 1).name("uRingPx");
    gui.add(this.uniformsA.uReflect, "value", 0, 2.0, 0.001).name("uReflect");

    gui.add({ replay: () => this._playSequence() }, "replay");
  }

  _render() {
     if (this.uniformsA) this.uniformsA.uTime.value += 0.016;
     if (this.uniformsB) this.uniformsB.uTime.value += 0.016;
    this._uTimeShared.value += 0.016;
  }

  onRaf() {
    this._render();
  }

  dispose() {
    window.removeEventListener("resize", this._onResize);
    if (this.tl) this.tl.kill();

    this.stage.scene.remove(this.group);

    const disposeMesh = (m) => {
      if (!m) return;
      m.material?.dispose?.();
    };

    disposeMesh(this.meshA);
    disposeMesh(this.meshB);

    if (this.bgMesh) {
      this.bgMesh.geometry.dispose();
      this.bgMesh.material.dispose();
      this.bgMesh = null;
    }
    if (this.logoMesh) {
      this.logoMesh.geometry.dispose();
      this.logoMesh.material.dispose();
      this.logoMesh = null;
    }

    this.meshA = null;
    this.meshB = null;

    if (this._texA) this._texA.dispose();
    if (this._texB) this._texB.dispose();
    if (this._texLogo) this._texLogo.dispose();

    if (this._videoTex) this._videoTex.dispose();
    if (this._videoEl) {
      this._videoEl.pause();
      this._videoEl.src = "";
      this._videoEl.load();
    }

    this.uniformsA = null;
    this.uniformsB = null;
  }
}
